import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, TrendingUp, Package, Filter, CheckCircle2, AlertCircle, BarChart3, Info } from 'lucide-react';
import CountUp from 'react-countup';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { useTaskDrawer } from '../hooks/useTaskDrawer';
import { TaskDrawer } from '../components/TaskDrawer';
import MonthlyChart from '../components/MonthlyChart';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  
  // Filters
  const [period, setPeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  
  // Drawer
  const { selectedTaskId, isOpen: showDrawer, drawerRef, openDrawer: handleTaskClick, closeDrawer: handleCloseDrawer } = useTaskDrawer();
  
  // Check if user can see user filter (not team member)

  // Check if user can see user filter (not team member)
  const canSelectUser = user && !['takım_üyesi', 'team_member'].includes(user.role);

  // Kullanıcı badge rengi — Görevler ekranındaki (TaskBoard) avatar renkleriyle birebir aynı
  // index mantığı (userId ilk karakter kodu). Gradient yerine yazı için solid hex döner.
  const getUserBadgeColor = (userId) => {
    const badgeTextColors = [
      '#9333ea', // 0 violet/purple
      '#db2777', // 1 rose/pink
      '#0d9488', // 2 emerald/teal
      '#4f46e5', // 3 blue/indigo
      '#ea580c', // 4 amber/orange
      '#0284c7', // 5 cyan/sky
      '#c026d3', // 6 fuchsia/pink
      '#16a34a', // 7 lime/green
    ];
    const colorIndex = (userId || '').charCodeAt(0) % badgeTextColors.length;
    return badgeTextColors[colorIndex];
  };

  const generatePDF = async () => {
    if (!reportData) return;
    
    const { jsPDF } = await import('jspdf');
    const autoTableModule = await import('jspdf-autotable');
    const autoTable = autoTableModule.default;
    const { poppinsMedium, poppinsBold } = await import('../utils/poppinsFont');
    
    const doc = new jsPDF('p', 'mm', 'a4');
    
    // Türkçe karakter desteği için Poppins fontunu yükle
    doc.addFileToVFS('Poppins-Medium.ttf', poppinsMedium);
    doc.addFileToVFS('Poppins-Bold.ttf', poppinsBold);
    doc.addFont('Poppins-Medium.ttf', 'Poppins', 'normal');
    doc.addFont('Poppins-Bold.ttf', 'Poppins', 'bold');
    doc.setFont('Poppins');
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    let y = 0;

    // --- Yardımcı: hex -> RGB ---
    const hexToRgb = (hex) => {
      const h = (hex || '#6366f1').replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };

    // --- Yardımcı: hafif renk tonu (beyazla karışım) ---
    const tintColor = (rgb, amount) => {
      return rgb.map(c => Math.round(255 - amount * (255 - c)));
    };

    // --- Tag renk haritası oluştur ---
    const tagColorMap = {};
    (reportData.tagSummaries || []).forEach(ts => {
      tagColorMap[ts.tagId] = { name: ts.tagName, color: ts.tagColor || '#6366f1' };
    });
    // tags state'inden de ekle (tagSummaries'de olmayanlar için)
    tags.forEach(t => {
      if (!tagColorMap[t.id]) tagColorMap[t.id] = { name: t.name, color: t.color || '#6366f1' };
    });

    // --- Görev -> Birincil kategori bulma ---
    const getTaskPrimaryTag = (task) => {
      const history = task.assignmentHistory || [];
      // Tamamlanmış adımlardan en çok tekrar eden completedTagId
      const tagCounts = {};
      history.forEach(a => {
        if (a.completedAt && a.completedTagId) {
          tagCounts[a.completedTagId] = (tagCounts[a.completedTagId] || 0) + 1;
        }
      });
      // task-level tagId fallback
      if (Object.keys(tagCounts).length === 0 && task.tagId) {
        return tagColorMap[task.tagId] || null;
      }
      const topTagId = Object.entries(tagCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
      return topTagId ? tagColorMap[topTagId] : null;
    };

    // --- Filtre bilgilerini hazırla ---
    const periodLabels = { 'all': 'Tüm Zamanlar', 'this_month': 'Bu Ay', 'prev_month': 'Önceki Ay', 'this_year': 'Bu Yıl', 'prev_year': 'Önceki Yıl', 'custom': 'Özel Aralık' };
    const selectedProjectName = selectedProject === 'all' ? 'Tüm Projeler' : (projects.find(p => p.id === selectedProject)?.name || '');
    const selectedUserName = selectedUser === 'all' ? 'Tüm Kullanıcılar' : (availableUsers.find(u => u.id === selectedUser)?.name || '');
    const selectedTagName = selectedTag === 'all' ? 'Tüm Kategoriler' : (tags.find(t => t.id === selectedTag)?.name || '');

    // =============================================
    // HEADER - Koyu arka plan, ince alt çizgi aksanı
    // =============================================
    doc.setFillColor(17, 24, 39); // slate-900
    doc.rect(0, 0, pageWidth, 36, 'F');
    // Aksan çizgisi
    doc.setFillColor(6, 182, 212); // cyan-500
    doc.rect(0, 36, pageWidth, 1.2, 'F');

    // Sol: Logo + başlık
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('Poppins', 'bold');
    doc.text('ONXFLOW', margin, 15);
    doc.setFontSize(10);
    doc.setFont('Poppins', 'normal');
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text('Detaylı Rapor', margin, 23);

    // Sağ: dönem
    doc.setFontSize(9);
    doc.setTextColor(6, 182, 212);
    doc.setFont('Poppins', 'bold');
    const periodText = period === 'custom' && customStartDate && customEndDate
      ? `${customStartDate.toLocaleDateString('tr-TR')} - ${customEndDate.toLocaleDateString('tr-TR')}`
      : (periodLabels[period] || period);
    doc.text(periodText, pageWidth - margin, 18, { align: 'right' });
    y = 42;

    // =============================================
    // FİLTRE SATIRI
    // =============================================
    doc.setFillColor(241, 245, 249); // slate-100
    doc.roundedRect(margin, y, pageWidth - margin * 2, 9, 1.5, 1.5, 'F');
    doc.setTextColor(71, 85, 105); // slate-600
    doc.setFontSize(7.5);
    doc.setFont('Poppins', 'normal');
    const filterText = `Proje: ${selectedProjectName}   |   Kullanıcı: ${selectedUserName}   |   Kategori: ${selectedTagName}`;
    doc.text(filterText, margin + 4, y + 6);
    y += 14;

    // =============================================
    // ÖZET İSTATİSTİKLER (Toplam Görev, Süre, Adet)
    // =============================================
    const statBoxWidth = (pageWidth - margin * 2 - 8) / 3;
    const stats = [
      { label: 'Toplam Görev', value: String(reportData.totalTasks || 0), accent: [6, 182, 212] },
      { label: 'Toplam Süre', value: reportData.totalDuration || '00:00:00', accent: [124, 58, 237] },
      { label: 'Toplam Adet', value: String(reportData.totalQuantity || 0), accent: [234, 88, 12] }
    ];
    stats.forEach((stat, i) => {
      const sx = margin + i * (statBoxWidth + 4);
      // Kutu
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240); // slate-200
      doc.setLineWidth(0.4);
      doc.roundedRect(sx, y, statBoxWidth, 18, 2, 2, 'FD');
      // Sol aksanlı çizgi
      doc.setFillColor(...stat.accent);
      doc.roundedRect(sx, y, 1.5, 18, 1, 1, 'F');
      // Etiket
      doc.setFontSize(7);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(stat.label, sx + 6, y + 6.5);
      // Değer
      doc.setFontSize(13);
      doc.setFont('Poppins', 'bold');
      doc.setTextColor(...stat.accent);
      doc.text(stat.value, sx + 6, y + 14.5);
    });
    y += 24;

    // =============================================
    // KATEGORİ ÖZET KARTLARI
    // =============================================
    const visibleTags = (reportData.tagSummaries || []).filter(t => t.taskCount > 0 && (t.totalQuantity > 0 || t.totalDurationSeconds > 0));
    
    if (visibleTags.length > 0) {
      // Bölüm başlığı
      doc.setFillColor(6, 182, 212);
      doc.roundedRect(margin, y, 3, 6, 1, 1, 'F');
      doc.setTextColor(17, 24, 39);
      doc.setFontSize(12);
      doc.setFont('Poppins', 'bold');
      doc.text('Kategori Bazlı Detaylar', margin + 6, y + 5);
      y += 10;

      const cardWidth = (pageWidth - margin * 2 - 8) / 3;
      const cardHeight = 28;

      visibleTags.forEach((tag, idx) => {
        const col = idx % 3;
        const row = Math.floor(idx / 3);
        const cx = margin + col * (cardWidth + 4);
        const cy = y + row * (cardHeight + 3);

        if (cy + cardHeight > pageHeight - 20) {
          doc.addPage();
          y = margin;
          return;
        }

        const [r, g, b] = hexToRgb(tag.tagColor);

        // Kart arka planı - hafif renk tonu
        doc.setFillColor(...tintColor([r, g, b], 0.08));
        doc.setDrawColor(r, g, b);
        doc.setLineWidth(0.3);
        doc.roundedRect(cx, cy, cardWidth, cardHeight, 2, 2, 'FD');
        // Sol kalın renk şeridi
        doc.setFillColor(r, g, b);
        doc.roundedRect(cx, cy, 2, cardHeight, 1, 1, 'F');

        // Kategori ismi
        doc.setTextColor(17, 24, 39);
        doc.setFontSize(9);
        doc.setFont('Poppins', 'bold');
        doc.text(tag.tagName || '', cx + 6, cy + 7);

        // Görev sayısı badge
        doc.setFillColor(r, g, b);
        const countText = String(tag.taskCount);
        const countWidth = doc.getTextWidth(countText) + 5;
        doc.roundedRect(cx + cardWidth - countWidth - 4, cy + 3, countWidth, 5.5, 1.5, 1.5, 'F');
        doc.setFontSize(7);
        doc.setFont('Poppins', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(countText, cx + cardWidth - countWidth / 2 - 4 + 0.5, cy + 7, { align: 'center' });

        // Alt satır: Süre + Adet
        let bx = cx + 6;
        const by = cy + 15;

        if (tag.totalDuration && tag.totalDuration !== '00:00:00') {
          doc.setFontSize(7);
          doc.setFont('Poppins', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text('Süre:', bx, by);
          doc.setFont('Poppins', 'bold');
          doc.setTextColor(124, 58, 237);
          doc.text(tag.totalDuration, bx + 12, by);
          bx += 35;
        }

        if (tag.totalQuantity > 0) {
          doc.setFontSize(7);
          doc.setFont('Poppins', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text('Adet:', bx, by);
          doc.setFont('Poppins', 'bold');
          doc.setTextColor(234, 88, 12);
          doc.text(String(tag.totalQuantity), bx + 12, by);
        }

        // İkinci satır: Toplam süre/adet daha büyük gösterim
        if (tag.totalDuration && tag.totalDuration !== '00:00:00' && tag.totalQuantity > 0) {
          // İkisi de varsa ikinci satır yok, ilk satırda gösterdik
        }
      });

      const totalRows = Math.ceil(visibleTags.length / 3);
      y += totalRows * (cardHeight + 3) + 6;
    }

    // Sayfa taşması kontrolü
    if (y > pageHeight - 40) {
      doc.addPage();
      y = margin;
    }

    // =============================================
    // GÖREV TABLOSU - Kategori renkli
    // =============================================
    const taskRows = (reportData.tasks || []).filter(task => {
      const completedSteps = task.assignmentHistory?.filter(a => {
        if (!a.completedAt) return false;
        if (selectedUser !== 'all') return a.userId === selectedUser;
        return true;
      }) || [];
      return completedSteps.length > 0;
    });

    if (taskRows.length > 0) {
      // Bölüm başlığı
      doc.setFillColor(6, 182, 212);
      doc.roundedRect(margin, y, 3, 6, 1, 1, 'F');
      doc.setTextColor(17, 24, 39);
      doc.setFontSize(12);
      doc.setFont('Poppins', 'bold');
      doc.text(`Görevler (${taskRows.length})`, margin + 6, y + 5);
      y += 10;

      const statusLabels = { 'beklemede': 'Beklemede', 'işlemde': 'İşlemde', 'incelemede': 'İncelemede', 'tamamlandı': 'Tamamlandı' };
      const statusColors = {
        'beklemede': [156, 163, 175],
        'işlemde': [59, 130, 246],
        'incelemede': [245, 158, 11],
        'tamamlandı': [34, 197, 94]
      };

      // ✅ Görevleri en son işlem tarihine göre sırala (yeniden eskiye)
      taskRows.sort((a, b) => {
        const getLastDate = (task) => (task.assignmentHistory || []).reduce((latest, entry) => {
          const dates = [entry.completedAt, entry.assignedAt, entry.reassignedAt].filter(Boolean);
          for (const d of dates) { if (!latest || d > latest) latest = d; }
          return latest;
        }, null);
        const dateA = getLastDate(a) || '';
        const dateB = getLastDate(b) || '';
        return dateB.localeCompare(dateA);
      });

      // Tablo verisi: Kategori sütunu eklendi
      const rowCompletedBy = [];
      const tableData = taskRows.map(task => {
        const project = projects.find(p => p.id === task.projectId);
        const completedSteps = task.assignmentHistory?.filter(a => {
          if (!a.completedAt) return false;
          if (selectedUser !== 'all') return a.userId === selectedUser;
          return true;
        }) || [];
        const primaryTag = getTaskPrimaryTag(task);

        const lastActionDate = (task.assignmentHistory || []).reduce((latest, a) => {
          const dates = [a.completedAt, a.assignedAt, a.reassignedAt].filter(Boolean);
          for (const d of dates) {
            if (!latest || d > latest) latest = d;
          }
          return latest;
        }, null);

        // Kategori seçiliyse, görev adının altına o kategorideki adımı tamamlayan
        // kullanıcı adı (kendi badge renginde) çizilir. Renkli kısım elle çizildiği için
        // ayrı bir dizide tutulur.
        const completedByList = [];
        if (selectedTag !== 'all') {
          // Ekranla aynı kural: kullanıcı+kategori başına EN ESKİ tamamlanmış kaydın
          // tek değeri (revizyon adımları toplanmaz).
          const byUser = {};
          (task.assignmentHistory || [])
            .filter(a => a.completedAt && a.completedTagId === selectedTag)
            .filter(a => selectedUser === 'all' || a.userId === selectedUser)
            .forEach(a => {
              const u = availableUsers.find(x => x.id === a.userId);
              if (!u) return;
              const cur = byUser[u.id];
              if (!cur || a.completedAt < cur.completedAt) {
                byUser[u.id] = {
                  name: u.name, color: getUserBadgeColor(u.id),
                  completedAt: a.completedAt,
                  qType: a.completedQuantityType, qValue: a.completedQuantityValue,
                };
              }
            });
          Object.values(byUser).forEach(e => {
            let qtyLabel = '';
            if (e.qType === 'süre' && e.qValue) qtyLabel = String(e.qValue);
            else if (e.qType === 'adet' && e.qValue) qtyLabel = `${e.qValue} adet`;
            completedByList.push({ name: e.name, color: e.color, qtyLabel });
          });
        }
        rowCompletedBy.push(completedByList);

        return [
          task.title || '',
          project?.name || '-',
          statusLabels[task.status] || task.status || '',
          `${completedSteps.length}`,
          lastActionDate ? new Date(lastActionDate).toLocaleDateString('tr-TR') : '-'
        ];
      });

      const rowStatusKeys = taskRows.map(task => task.status);

      autoTable(doc, {
        startY: y,
        head: [['Görev', 'Proje', 'Durum', 'Adım', 'Tarih']],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 7.5,
          cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
          overflow: 'linebreak',
          font: 'Poppins',
          textColor: [51, 65, 85],
          lineColor: [226, 232, 240],
          lineWidth: 0.2
        },
        headStyles: {
          fillColor: [17, 24, 39],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 7.5,
          cellPadding: { top: 3.5, right: 2, bottom: 3.5, left: 2 }
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 80, valign: 'top' },
          1: { cellWidth: 44 },
          2: { cellWidth: 22, halign: 'center' },
          3: { cellWidth: 14, halign: 'center' },
          4: { cellWidth: 22, halign: 'center' }
        },
        didParseCell: (data) => {
          // Görev sütununda tamamlayan kullanıcı varsa, alta isim satırı için yer ayır
          if (data.section === 'body' && data.column.index === 0) {
            const cb = rowCompletedBy[data.row.index];
            if (cb && cb.length) {
              data.cell.styles.cellPadding = { top: 3, right: 2, bottom: 8, left: 2 };
            }
          }
        },
        willDrawCell: (data) => {
          if (data.section === 'body') {
            const rowIdx = data.row.index;
            // Durum sütunu (index 2): durum rengine göre arka plan
            if (data.column.index === 2) {
              const statusKey = rowStatusKeys[rowIdx];
              const sc = statusColors[statusKey];
              if (sc) {
                doc.setFillColor(...tintColor(sc, 0.12));
              }
            }
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'body') {
            const rowIdx = data.row.index;
            // Durum sütunu: sol kenarına durum renk çizgisi
            if (data.column.index === 2) {
              const statusKey = rowStatusKeys[rowIdx];
              const sc = statusColors[statusKey];
              if (sc) {
                doc.setFillColor(sc[0], sc[1], sc[2]);
                doc.rect(data.cell.x, data.cell.y, 1, data.cell.height, 'F');
              }
            }
            // Görev sütunu: tamamlayan kullanıcı adını alta, kendi renginde + ok ile çiz
            if (data.column.index === 0) {
              const cb = rowCompletedBy[rowIdx];
              if (cb && cb.length) {
                const baseX = data.cell.x + 2;
                const baseY = data.cell.y + data.cell.height - 2.8;
                // Vektör ok (chevron »› benzeri) - fonttan bağımsız çizim
                doc.setDrawColor(148, 163, 184);
                doc.setLineWidth(0.45);
                const cy = baseY - 1.1;
                doc.line(baseX, cy - 1.1, baseX + 1.5, cy);
                doc.line(baseX + 1.5, cy, baseX, cy + 1.1);
                // İsimler, her biri kendi renginde
                doc.setFont('Poppins', 'bold');
                doc.setFontSize(7.5);
                let nx = baseX + 3.2;
                cb.forEach((item, i) => {
                  const rgb = hexToRgb(item.color);
                  doc.setTextColor(rgb[0], rgb[1], rgb[2]);
                  const nameTxt = (i > 0 ? ', ' : '') + item.name;
                  doc.text(nameTxt, nx, baseY);
                  nx += doc.getTextWidth(nameTxt);
                  // Miktar bilgisi (gri) — kullanıcı adının hemen yanına
                  if (item.qtyLabel) {
                    doc.setTextColor(100, 116, 139);
                    const qtyTxt = ` · ${item.qtyLabel}`;
                    doc.text(qtyTxt, nx, baseY);
                    nx += doc.getTextWidth(qtyTxt);
                  }
                });
                // Stilleri sıfırla
                doc.setTextColor(51, 65, 85);
                doc.setFont('Poppins', 'normal');
              }
            }
          }
        },
        didDrawPage: () => {
          // Alt çizgi her sayfada
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.3);
          doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
        }
      });
    }

    // =============================================
    // FOOTER - Tüm sayfalara
    // =============================================
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      // Alt çizgi
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      // Sol: marka
      doc.setFontSize(6.5);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text('ONXFLOW', margin, pageHeight - 7);
      // Orta: tarih
      doc.text(new Date().toLocaleDateString('tr-TR'), pageWidth / 2, pageHeight - 7, { align: 'center' });
      // Sağ: sayfa
      doc.setFont('Poppins', 'bold');
      doc.text(`${i} / ${totalPages}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
    }

    // PDF'i yeni sekmede aç
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  // =============================================
  // KULLANICI PERFORMANS PDF
  // =============================================
  const generatePerformancePDF = async () => {
    if (!reportData || !reportData.tasks) return;

    const { jsPDF } = await import('jspdf');
    const autoTableModule = await import('jspdf-autotable');
    const autoTable = autoTableModule.default;
    const { poppinsMedium, poppinsBold } = await import('../utils/poppinsFont');

    const doc = new jsPDF('p', 'mm', 'a4');
    doc.addFileToVFS('Poppins-Medium.ttf', poppinsMedium);
    doc.addFileToVFS('Poppins-Bold.ttf', poppinsBold);
    doc.addFont('Poppins-Medium.ttf', 'Poppins', 'normal');
    doc.addFont('Poppins-Bold.ttf', 'Poppins', 'bold');
    doc.setFont('Poppins');

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;

    const hexToRgb = (hex) => {
      const h = (hex || '#6366f1').replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };
    const tintColor = (rgb, amount) => rgb.map(c => Math.round(255 - amount * (255 - c)));

    // --- Tag renk/isim haritası ---
    const tagMap = {};
    (reportData.tagSummaries || []).forEach(ts => {
      tagMap[ts.tagId] = { name: ts.tagName, color: ts.tagColor || '#6366f1' };
    });
    tags.forEach(t => {
      if (!tagMap[t.id]) tagMap[t.id] = { name: t.name, color: t.color || '#6366f1' };
    });

    // --- User isim haritası ---
    const userMap = {};
    (availableUsers || []).forEach(u => { userMap[u.id] = u.name; });

    // --- Kullanıcı bazlı performans: backend'in DOĞRU hesabını (onay tarihi + dedup) kullan ---
    // ÖNEMLİ: Eskiden burada `reportData.tasks` üzerinden ham `completedAt` ile yeniden
    // hesaplama yapılıyordu. Bu; (1) onay tarihi yerine ham tamamlanma tarihini kullanması ve
    // (2) tarih filtresini dedup'tan ÖNCE uygulayıp revizyon kayıtlarını gerçek üretim kaydının
    // yerine geçirmesi nedeniyle backend ile tutarsızlık üretiyordu (ör. SZT 13:57 yerine 16:16).
    // Artık tek doğruluk kaynağı backend.userSummaries — ekrandaki tek-kullanıcı değeriyle birebir aynı.
    const rows = (reportData.userSummaries || [])
      .filter(s => (s.totalDurationSeconds > 0 || s.totalQuantity > 0))
      .map(s => {
        const isSure = s.quantityType === 'süre' || s.quantityType === 'sure';
        const tagFallback = tagMap[s.tagId] || {};
        return {
          userId: s.userId,
          userName: userMap[s.userId] || s.userName || s.userId,
          tagId: s.tagId,
          tagName: s.tagName || tagFallback.name || 'Kategorisiz',
          tagColor: s.tagColor || tagFallback.color || '#9ca3af',
          qType: s.quantityType || 'adet',
          totalValue: isSure ? (s.totalDurationSeconds || 0) : (s.totalQuantity || 0),
          taskCount: s.taskCount || 0,
        };
      })
      .sort((a, b) => {
        const tagCompare = a.tagName.localeCompare(b.tagName, 'tr');
        return tagCompare !== 0 ? tagCompare : a.userName.localeCompare(b.userName, 'tr');
      });

    if (rows.length === 0) {
      toast.error('Seçilen filtrelere uygun kullanıcı performans verisi bulunamadı.');
      return;
    }

    // --- Filtre bilgileri ---
    const periodLabels = { 'all': 'Tüm Zamanlar', 'this_month': 'Bu Ay', 'prev_month': 'Önceki Ay', 'this_year': 'Bu Yıl', 'prev_year': 'Önceki Yıl', 'custom': 'Özel Aralık' };
    const periodText = period === 'custom' && customStartDate && customEndDate
      ? `${customStartDate.toLocaleDateString('tr-TR')} - ${customEndDate.toLocaleDateString('tr-TR')}`
      : (periodLabels[period] || period);
    const selectedProjectName = selectedProject === 'all' ? 'Tüm Projeler' : (projects.find(p => p.id === selectedProject)?.name || '');
    const selectedTagName = selectedTag === 'all' ? 'Tüm Kategoriler' : (tags.find(t => t.id === selectedTag)?.name || '');

    // Süre formatlama - HH:MM:SS
    const formatDuration = (totalSeconds) => {
      if (!totalSeconds || totalSeconds <= 0) return '-';
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = Math.floor(totalSeconds % 60);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(h)}:${pad(m)}:${pad(s)}`;
    };

    let y = 0;

    // =============================================
    // HEADER
    // =============================================
    doc.setFillColor(17, 24, 39);
    doc.rect(0, 0, pageWidth, 36, 'F');
    doc.setFillColor(234, 88, 12); // orange-600 accent
    doc.rect(0, 36, pageWidth, 1.2, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('Poppins', 'bold');
    doc.text('ONXFLOW', margin, 15);
    doc.setFontSize(10);
    doc.setFont('Poppins', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text('Kullanıcı Performans Raporu', margin, 23);

    doc.setFontSize(9);
    doc.setTextColor(234, 88, 12);
    doc.setFont('Poppins', 'bold');
    doc.text(periodText, pageWidth - margin, 18, { align: 'right' });
    y = 42;

    // =============================================
    // FİLTRE SATIRI
    // =============================================
    doc.setFillColor(255, 247, 237); // orange-50
    doc.roundedRect(margin, y, pageWidth - margin * 2, 9, 1.5, 1.5, 'F');
    doc.setTextColor(154, 52, 18); // orange-800
    doc.setFontSize(7.5);
    doc.setFont('Poppins', 'normal');
    doc.text(`Proje: ${selectedProjectName}   |   Kategori: ${selectedTagName}   |   Dönem: ${periodText}`, margin + 4, y + 6);
    y += 14;

    // =============================================
    // ÖZET KUTULARI
    // =============================================
    const uniqueUsers = [...new Set(rows.map(r => r.userId))].length;
    const uniqueCategories = [...new Set(rows.map(r => r.tagId))].length;
    const totalTasks = rows.reduce((s, r) => s + r.taskCount, 0);
    const statBoxWidth = (pageWidth - margin * 2 - 8) / 3;
    const summaryStats = [
      { label: 'Kullanıcı Sayısı', value: String(uniqueUsers), accent: [234, 88, 12] },
      { label: 'Kategori Sayısı', value: String(uniqueCategories), accent: [124, 58, 237] },
      { label: 'Toplam Görev Sayısı', value: String(reportData.totalTasks || 0), accent: [6, 182, 212] }
    ];
    summaryStats.forEach((stat, i) => {
      const sx = margin + i * (statBoxWidth + 4);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.4);
      doc.roundedRect(sx, y, statBoxWidth, 18, 2, 2, 'FD');
      doc.setFillColor(...stat.accent);
      doc.roundedRect(sx, y, 1.5, 18, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(stat.label, sx + 6, y + 6.5);
      doc.setFontSize(13);
      doc.setFont('Poppins', 'bold');
      doc.setTextColor(...stat.accent);
      doc.text(stat.value, sx + 6, y + 14.5);
    });
    y += 24;

    // =============================================
    // ÇUBUK GRAFİKLER - Kategori bazında
    // =============================================
    // Kategorilere göre grupla (grafik için)
    const chartGroups = {};
    rows.forEach(r => {
      if (!chartGroups[r.tagId]) chartGroups[r.tagId] = { tagName: r.tagName, tagColor: r.tagColor, rows: [] };
      chartGroups[r.tagId].rows.push(r);
    });
    const sortedChartGroups = Object.values(chartGroups).sort((a, b) => a.tagName.localeCompare(b.tagName, 'tr'));

    sortedChartGroups.forEach(group => {
      const sorted = [...group.rows].sort((a, b) => b.totalValue - a.totalValue);
      const isSure = sorted.some(r => r.qType === 'süre' || r.qType === 'sure');
      const [cr, cg, cb] = hexToRgb(group.tagColor);
      const maxVal = Math.max(...sorted.map(r => r.totalValue), 1);
      const barCount = sorted.length;

      const chartHeight = 45;
      const chartLeft = margin + 30;
      const chartRight = pageWidth - margin;
      const chartWidth = chartRight - chartLeft;
      const barGap = 4;
      const barWidth = Math.min(28, (chartWidth - barGap * (barCount + 1)) / barCount);
      const totalBarsWidth = barCount * barWidth + (barCount - 1) * barGap;
      const startX = chartLeft + (chartWidth - totalBarsWidth) / 2;

      // Sayfa taşması kontrolü
      if (y + chartHeight + 28 > pageHeight - 20) {
        doc.addPage();
        y = margin;
      }

      // Kategori başlığı
      doc.setFillColor(cr, cg, cb);
      doc.roundedRect(margin, y, 3, 5, 1, 1, 'F');
      doc.setTextColor(17, 24, 39);
      doc.setFontSize(9);
      doc.setFont('Poppins', 'bold');
      const typeLabel = isSure ? '(Süre)' : '(Adet)';
      doc.text(`${group.tagName} ${typeLabel}`, margin + 6, y + 4);
      y += 8;

      // Y ekseni çizgileri ve etiketleri
      const yAxisSteps = 4;
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.2);
      for (let i = 0; i <= yAxisSteps; i++) {
        const lineY = y + chartHeight - (chartHeight * i / yAxisSteps);
        doc.line(chartLeft - 2, lineY, chartRight, lineY);
        // Y ekseni etiketi
        const labelVal = maxVal * i / yAxisSteps;
        doc.setFontSize(6);
        doc.setFont('Poppins', 'normal');
        doc.setTextColor(120, 120, 120);
        const label = isSure ? formatDuration(labelVal) : String(Math.round(labelVal));
        doc.text(label, chartLeft - 4, lineY + 1, { align: 'right' });
      }

      // Çubuklar
      sorted.forEach((row, idx) => {
        const barH = maxVal > 0 ? (row.totalValue / maxVal) * chartHeight : 0;
        const bx = startX + idx * (barWidth + barGap);
        const by = y + chartHeight - barH;

        // Çubuk gövdesi - gradient efekti
        doc.setFillColor(cr, cg, cb);
        doc.roundedRect(bx, by, barWidth, barH, 1, 1, 'F');
        // Hafif üst parlaklık
        const lightR = Math.min(255, cr + 40);
        const lightG = Math.min(255, cg + 40);
        const lightB = Math.min(255, cb + 40);
        doc.setFillColor(lightR, lightG, lightB);
        doc.roundedRect(bx, by, barWidth, Math.min(barH, 3), 1, 1, 'F');

        // Değer etiketi (çubuğun üstünde)
        doc.setFontSize(6);
        doc.setFont('Poppins', 'bold');
        doc.setTextColor(cr, cg, cb);
        const valLabel = isSure ? formatDuration(row.totalValue) : String(row.totalValue);
        doc.text(valLabel, bx + barWidth / 2, by - 2, { align: 'center' });

        // Kullanıcı adı (altta, kısa)
        doc.setFontSize(5.5);
        doc.setFont('Poppins', 'normal');
        doc.setTextColor(80, 80, 80);
        const shortName = row.userName.length > 8 ? row.userName.substring(0, 7) + '..' : row.userName;
        doc.text(shortName, bx + barWidth / 2, y + chartHeight + 5, { align: 'center' });
      });

      y += chartHeight + 12;
    });

    // Grafik-tablo arası boşluk
    y += 4;

    // =============================================
    // KULLANICI PERFORMANS TABLOSU
    // =============================================
    doc.setFillColor(234, 88, 12);
    doc.roundedRect(margin, y, 3, 6, 1, 1, 'F');
    doc.setTextColor(17, 24, 39);
    doc.setFontSize(12);
    doc.setFont('Poppins', 'bold');
    doc.text('Kullanıcı Performans Detayları', margin + 6, y + 5);
    y += 10;

    // Tablo verisi - kategorik tasnifli
    const tableData = [];
    const rowMeta = [];
    const rowColors = [];
    
    // Kategorilere göre grupla
    const grouped = {};
    rows.forEach(r => {
      if (!grouped[r.tagId]) grouped[r.tagId] = { tagId: r.tagId, tagName: r.tagName, tagColor: r.tagColor, rows: [] };
      grouped[r.tagId].rows.push(r);
    });

    // Kategori ismine göre sırala
    const sortedGroups = Object.values(grouped).sort((a, b) => a.tagName.localeCompare(b.tagName, 'tr'));

    // Değer formatlama: satırın miktar tipine göre doğru sütuna yaz
    const formatRowValue = (value, isSure) => {
      if (isSure) {
        return [value > 0 ? formatDuration(value) : '-', '-'];
      } else {
        return ['-', value > 0 ? String(value) : '-'];
      }
    };

    // Tutar formatlama
    const formatTutar = (amount) => {
      if (!amount || amount === 0) return '-';
      return Math.round(amount).toLocaleString('tr-TR') + ' ₺';
    };

    // Tutar hesaplama: adet ise adet×birimFiyat, süre ise dakika×birimFiyat
    const calculateTutar = (totalValue, isSure, tagId) => {
      const tag = tags.find(t => t.id === tagId);
      const unitPrice = tag?.unitPrice || 0;
      if (unitPrice === 0) return 0;
      if (isSure) {
        const minutes = totalValue / 60; // saniyeyi dakikaya çevir
        return minutes * unitPrice;
      }
      return totalValue * unitPrice;
    };

    let grandTotalSure = 0;
    let grandTotalAdet = 0;
    let grandTotalTutar = 0;

    sortedGroups.forEach(group => {
      const [r, g, b] = hexToRgb(group.tagColor);
      // Kategorideki ilk satırdan miktar tipini belirle
      const isSure = group.rows.some(rx => rx.qType === 'süre' || rx.qType === 'sure');

      // Kategori başlık satırı - miktar tipini göster
      const typeLabel = isSure ? '(Süre)' : '(Adet)';
      tableData.push([group.tagName + ' ' + typeLabel, '', '', '', '']);
      rowMeta.push('header');
      rowColors.push([r, g, b]);

      let catTutar = 0;

      // Kullanıcı satırları (değere göre büyükten küçüğe)
      group.rows.sort((a, bx) => bx.totalValue - a.totalValue);
      group.rows.forEach((row, idx) => {
        const rowIsSure = row.qType === 'süre' || row.qType === 'sure';
        const [sureVal, adetVal] = formatRowValue(row.totalValue, rowIsSure);
        const rowTutar = calculateTutar(row.totalValue, rowIsSure, group.tagId);
        catTutar += rowTutar;
        tableData.push([
          '   ' + row.userName,
          row.taskCount > 0 ? String(row.taskCount) : '',
          sureVal,
          adetVal,
          formatTutar(rowTutar)
        ]);
        rowMeta.push(idx === 0 ? 'data_first' : 'data');
        rowColors.push([r, g, b]);
      });

      // Kategori ara toplamı - backend tagSummaries'den al (kullanıcı filtresi yoksa)
      let catSure, catAdet;
      const backendTag = selectedUser === 'all' 
        ? (reportData.tagSummaries || []).find(ts => ts.tagId === group.tagId)
        : null;
      
      if (backendTag && isSure) {
        catSure = backendTag.totalDuration || formatDuration(backendTag.totalDurationSeconds || 0);
        catAdet = '-';
        grandTotalSure += (backendTag.totalDurationSeconds || 0);
        // Tutar: backend toplamından hesapla
        catTutar = calculateTutar(backendTag.totalDurationSeconds || 0, true, group.tagId);
      } else if (backendTag && !isSure) {
        catSure = '-';
        catAdet = backendTag.totalQuantity > 0 ? String(backendTag.totalQuantity) : '-';
        grandTotalAdet += (backendTag.totalQuantity || 0);
        catTutar = calculateTutar(backendTag.totalQuantity || 0, false, group.tagId);
      } else {
        // Kullanıcı filtresi varsa, frontend'den hesapla
        const catTotal = group.rows.reduce((s, rx) => s + rx.totalValue, 0);
        const vals = formatRowValue(catTotal, isSure);
        catSure = vals[0];
        catAdet = vals[1];
        if (isSure) grandTotalSure += catTotal;
        else grandTotalAdet += catTotal;
        // catTutar zaten yukarıda satır satır toplandı
      }

      grandTotalTutar += catTutar;
      const catTaskCount = group.rows.reduce((s, rx) => s + (rx.taskCount || 0), 0);

      tableData.push([
        '   Ara Toplam',
        catTaskCount > 0 ? String(catTaskCount) : '',
        catSure,
        catAdet,
        formatTutar(catTutar)
      ]);
      rowMeta.push('subtotal');
      rowColors.push([r, g, b]);
    });

    // Genel toplam satırı
    const grandTaskCount = rows.reduce((s, r) => s + (r.taskCount || 0), 0);
    tableData.push([
      'GENEL TOPLAM',
      grandTaskCount > 0 ? String(grandTaskCount) : '',
      grandTotalSure > 0 ? formatDuration(grandTotalSure) : '-',
      grandTotalAdet > 0 ? String(grandTotalAdet) : '-',
      formatTutar(grandTotalTutar)
    ]);

    rowMeta.push('total');
    rowColors.push([17, 24, 39]);

    autoTable(doc, {
      startY: y,
      head: [['Kullanıcı / Kategori', 'Görev Sayısı', 'Toplam Süre', 'Toplam Adet', 'Toplam Tutar']],
      body: tableData,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 8,
        cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
        overflow: 'linebreak',
        font: 'Poppins',
        textColor: [51, 65, 85],
        lineColor: [226, 232, 240],
        lineWidth: 0.2
      },
      headStyles: {
        fillColor: [17, 24, 39],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: 62 },
        1: { cellWidth: 25, halign: 'right' },
        2: { cellWidth: 30, halign: 'right' },
        3: { cellWidth: 28, halign: 'right' },
        4: { cellWidth: 37, halign: 'right' }
      },
      didParseCell: (data) => {
        if (data.section === 'head' && (data.column.index >= 1)) {
          data.cell.styles.halign = 'right';
        }
        if (data.section === 'body') {
          const rowIdx = data.row.index;
          const meta = rowMeta[rowIdx];
          if (meta === 'header') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 10;
          } else if (meta === 'data_first') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 10;
          } else if (meta === 'subtotal') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 10;
          } else if (meta === 'total') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 11;
          }
        }
      },
      willDrawCell: (data) => {
        if (data.section === 'body') {
          const rowIdx = data.row.index;
          const meta = rowMeta[rowIdx];
          const color = rowColors[rowIdx];

          if (meta === 'header') {
            doc.setFillColor(...tintColor(color, 0.15));
            doc.setTextColor(color[0], color[1], color[2]);
          } else if (meta === 'data_first') {
            doc.setFillColor(...tintColor(color, 0.06));
          } else if (meta === 'subtotal') {
            doc.setFillColor(...tintColor(color, 0.08));
            doc.setTextColor(color[0], color[1], color[2]);
          } else if (meta === 'total') {
            doc.setFillColor(17, 24, 39);
            doc.setTextColor(255, 255, 255);
          } else if (meta === 'data') {
            // Normal veri satırı - alternatif renklendirme
            if (data.row.index % 2 === 0) {
              doc.setFillColor(255, 255, 255);
            } else {
              doc.setFillColor(248, 250, 252);
            }
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body') {
          const rowIdx = data.row.index;
          const meta = rowMeta[rowIdx];
          const color = rowColors[rowIdx];
          // Kategori başlık satırı: sol kenar kalın renk çizgisi
          if (meta === 'header' && data.column.index === 0) {
            doc.setFillColor(color[0], color[1], color[2]);
            doc.rect(data.cell.x, data.cell.y, 2, data.cell.height, 'F');
          }
        }
      }
    });

    // =============================================
    // FOOTER
    // =============================================
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      doc.setFontSize(6.5);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text('ONXFLOW', margin, pageHeight - 7);
      doc.text(new Date().toLocaleDateString('tr-TR'), pageWidth / 2, pageHeight - 7, { align: 'center' });
      doc.setFont('Poppins', 'bold');
      doc.text(`${i} / ${totalPages}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
    }

    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchProjects(), 
        fetchTags(),
        fetchWorkflows(),
        canSelectUser && fetchAvailableUsers()
      ].filter(Boolean));
      fetchReport();
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (period === 'custom' && (!customStartDate || !customEndDate)) return;
    fetchReport();
  }, [period, selectedProject, selectedTag, selectedUser, customStartDate, customEndDate]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      // ✅ Alfabetik sıralama
      const sortedProjects = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      setProjects(sortedProjects);
    } catch (error) {
      console.error('Projeler yüklenemedi:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      // ✅ Alfabetik sıralama
      const sortedTags = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      setTags(sortedTags);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`);
      setWorkflows(response.data || []);
    } catch (error) {
      // Yetkisi olmayan kullanıcılar için sessizce geç
      console.warn('İş akışları yüklenemedi:', error?.response?.status);
      setWorkflows([]);
    }
  };

  const fetchAvailableUsers = async () => {
    if (!canSelectUser) return;
    
    try {
      const response = await axios.get(`${API}/reports/available-users`);
      // ✅ Alfabetik sıralama
      const sortedUsers = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      setAvailableUsers(sortedUsers);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {
        period,
        ...(selectedProject !== 'all' && { project_id: selectedProject }),
        ...(selectedTag !== 'all' && { tag_id: selectedTag }),
        ...(selectedUser !== 'all' && { user_id: selectedUser }),
        ...(period === 'custom' && customStartDate && { start_date: customStartDate.toISOString() }),
        ...(period === 'custom' && customEndDate && { end_date: new Date(customEndDate.getTime() + 86400000 - 1).toISOString() })
      };

      console.log('Fetching report with params:', JSON.stringify(params, null, 2));
      const response = await axios.get(`${API}/reports/user-summary`, { params });
      console.log('Report data received:');
      console.log('  Total Tasks:', response.data.totalTasks);
      console.log('  Total Duration:', response.data.totalDuration);
      console.log('  Total Quantity:', response.data.totalQuantity);
      console.log('  Tag Summaries:', response.data.tagSummaries);
      console.log('  Tasks count:', response.data.tasks?.length);
      // ✅ Detaylı tag summaries logu
      if (response.data.tagSummaries) {
        console.log('\n📊 DETAYLI TAG SUMMARIES:');
        response.data.tagSummaries.forEach((tag, idx) => {
          console.log(`  ${idx + 1}. ${tag.tagName} (${tag.tagId}):`);
          console.log(`     - taskCount: ${tag.taskCount}`);
          console.log(`     - totalQuantity: ${tag.totalQuantity}`);
          console.log(`     - totalDuration: ${tag.totalDuration}`);
        });
      }
      setReportData(response.data);
    } catch (error) {
      toast.error('Rapor yüklenemedi');
      console.error('Rapor hatası:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (durationStr) => {
    // If it's already a formatted string (HH:MM:SS), return as is
    if (typeof durationStr === 'string' && durationStr.includes(':')) {
      return durationStr; // Return HH:MM:SS format directly
    }
    // Fallback for old format (minutes as number) - convert to HH:MM:SS
    if (typeof durationStr === 'number') {
      const hours = Math.floor(durationStr / 60);
      const mins = Math.round(durationStr % 60);
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
    }
    return '00:00:00';
  };

  const getPeriodLabel = (p) => {
    const labels = {
      all: 'Tüm Zamanlar',
      weekly: 'Haftalık',
      monthly: 'Aylık',
      yearly: 'Yıllık'
    };
    return labels[p] || 'Tüm Zamanlar';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Rapor yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            Raporlarım
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">Tamamlanmış ve onaylanmış görevlerinizin detaylı analizi</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-lg flex items-center gap-2 sm:gap-3">
        <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 hidden sm:block" />
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm text-blue-900 font-medium">
            Bu rapor sadece tamamlanmış ve onaylanmış görevlerinizi içermektedir.
          </p>
          <p className="text-xs text-blue-700 mt-0.5 sm:mt-1 hidden sm:block">
            Devam eden, henüz onaylanmayan ve beklemedeki görevler dahil edilmemiştir.
          </p>
        </div>
        {reportData && reportData.tagSummaries && reportData.tagSummaries.length > 0 && (
          <>
            <button
              onClick={generatePDF}
              data-testid="pdf-report-button"
              className="flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
              title="PDF Rapor"
            >
              <img src="/assets/icons/pdf-icon.png" alt="PDF Rapor" className="w-8 h-8 sm:w-9 sm:h-9" />
            </button>
            {user && user.role === 'admin' && (
              <button
                onClick={generatePerformancePDF}
                data-testid="performance-report-button"
                className="flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                title="Kullanıcı Performans Raporu"
              >
                <img src="/assets/icons/performance-icon.png" alt="Performans Raporu" className="w-8 h-8 sm:w-9 sm:h-9" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Filters */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-cyan-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filtreler</h2>
        </div>
        <div className={`grid grid-cols-1 ${canSelectUser ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
          <DateRangeFilter
            period={period}
            onPeriodChange={setPeriod}
            startDate={customStartDate}
            endDate={customEndDate}
            onStartDateChange={setCustomStartDate}
            onEndDateChange={setCustomEndDate}
          />
          <div>
            <Label>Proje</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Projeler</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canSelectUser && (
            <div>
              <Label>Kullanıcı</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
                  {availableUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Kategori</Label>
            <Select value={selectedTag} onValueChange={setSelectedTag}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {tags.map(tag => (
                  <SelectItem key={tag.id} value={tag.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {reportData && reportData.totalTasks === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Info className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Henüz Tamamlanmış Görev Yok</h3>
          <p className="text-gray-600">Seçtiğiniz filtreye uygun tamamlanmış görev bulunmamaktadır.</p>
        </div>
      ) : reportData && (
        <>
          {/* 📊 Aylık Üretim Grafiği — filtrelerin hemen altında, tam genişlik */}
          <MonthlyChart
            monthlyBreakdown={reportData.monthlyBreakdown}
            periodLabel={
              period === 'this_month' ? 'Bu Ay' :
              period === 'last_month' ? 'Geçen Ay' :
              period === 'last_3_months' ? 'Son 3 Ay' :
              period === 'last_6_months' ? 'Son 6 Ay' :
              period === 'this_year' ? 'Bu Yıl' :
              period === 'all' ? 'Tüm Zamanlar' : 'Filtrelenen Dönem'
            }
            onMonthClick={(monthKey) => {
              // monthKey format: "YYYY-MM"
              const [yy, mm] = (monthKey || '').split('-').map((x) => parseInt(x, 10));
              if (!yy || !mm) return;
              const start = new Date(yy, mm - 1, 1);
              const end = new Date(yy, mm, 0); // ayın son günü
              setPeriod('custom');
              setCustomStartDate(start);
              setCustomEndDate(end);
            }}
          />
          {/* Tag-Based Summary Cards */}
          {reportData.tagSummaries && reportData.tagSummaries.length > 0 && (
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-6 h-6 text-cyan-600" />
                <h2 className="text-2xl font-semibold text-gray-900">Kategori Bazlı Detaylar</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reportData.tagSummaries
                  .filter(tagSummary => 
                    tagSummary.taskCount > 0 && 
                    (tagSummary.totalQuantity > 0 || tagSummary.totalDurationSeconds > 0)
                  )  // ✅ FIX: taskCount > 0 VE (quantity > 0 VEYA duration > 0) olanları göster
                  .map((tagSummary, idx) => (
                  <div
                    key={tagSummary.tagId}
                    className="p-6 rounded-2xl border-2 hover:shadow-xl transition-all transform hover:scale-105"
                    style={{
                      backgroundColor: `${tagSummary.tagColor}15`,
                      borderColor: tagSummary.tagColor
                    }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="w-5 h-5 rounded-full"
                        style={{ backgroundColor: tagSummary.tagColor }}
                      />
                      <h3 className="font-bold text-lg text-gray-900">{tagSummary.tagName}</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Görev Sayısı:</span>
                        <span className="text-2xl font-bold text-gray-900">
                          <CountUp end={tagSummary.taskCount} duration={1.5} />
                        </span>
                      </div>
                      
                      {tagSummary.totalDuration && tagSummary.totalDuration !== '00:00:00' && (
                        <div className="pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-purple-600" />
                              <span className="text-sm text-gray-600">Toplam Süre:</span>
                            </div>
                            <span className="text-xl font-bold text-purple-600 font-mono">
                              {tagSummary.totalDuration}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {tagSummary.totalQuantity > 0 && (
                        <div className="pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-orange-600" />
                              <span className="text-sm text-gray-600">Toplam Adet:</span>
                            </div>
                            <span className="text-xl font-bold text-orange-600">
                              <CountUp end={tagSummary.totalQuantity} duration={1.5} decimals={tagSummary.totalQuantity % 1 !== 0 ? 1 : 0} />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Görev Listesi */}
          {reportData.tasks && reportData.tasks.length > 0 ? (
            <div className="glass rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span className="relative">
                  Görevler
                  {/* Görev sayısı badge */}
                  <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                    {reportData.tasks.length.toLocaleString('tr-TR')}
                  </span>
                </span>
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Görev</th>
                      <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proje</th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32 sm:w-40">Durum</th>
                      <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.tasks
                      .map((task) => {
                      // Kullanıcının tamamladığı (onay almış) adım sayısı (sadece bu kullanıcının kayıtları)
                      const completedSteps = task.assignmentHistory?.filter(a => {
                        if (!a.completedAt) return false;
                        if (selectedUser !== 'all') {
                          return a.userId === selectedUser;
                        }
                        return true;
                      }) || [];
                      
                      const userCompletedSteps = completedSteps.length;
                      
                      // Eğer hiç adım tamamlanmamışsa gösterme
                      if (userCompletedSteps === 0) return null;

                      // Görevin iş akışındaki TOPLAM adım sayısı
                      // (yoksa direkt görev ise assignmentHistory'deki benzersiz stepIndex sayısını kullan)
                      let totalWorkflowSteps = 0;
                      if (task.workflowId) {
                        const wf = workflows.find(w => w.id === task.workflowId);
                        totalWorkflowSteps = wf?.steps?.length || 0;
                      }
                      if (!totalWorkflowSteps) {
                        // fallback: assignmentHistory'deki benzersiz stepIndex sayısı
                        const uniqueSteps = new Set(
                          (task.assignmentHistory || [])
                            .map(a => a.stepIndex)
                            .filter(i => i !== null && i !== undefined && i >= 0)
                        );
                        totalWorkflowSteps = uniqueSteps.size || 1;
                      }

                      // İş akışındaki kaç adımın TAMAMLANDIĞI (tüm kullanıcılar dahil — proje ilerleme göstergesi)
                      const completedStepIndices = new Set(
                        (task.assignmentHistory || [])
                          .filter(a => a.completedAt && a.stepIndex !== null && a.stepIndex !== undefined && a.stepIndex >= 0)
                          .map(a => a.stepIndex)
                      );
                      const completedStepsAcrossAll = completedStepIndices.size;
                      
                      // En son işlem tarihi: assignmentHistory'deki en güncel completedAt veya assignedAt
                      const lastActionDate = (task.assignmentHistory || []).reduce((latest, a) => {
                        const dates = [a.completedAt, a.assignedAt, a.reassignedAt].filter(Boolean);
                        for (const d of dates) {
                          if (!latest || d > latest) latest = d;
                        }
                        return latest;
                      }, null);
                      
                      const project = projects.find(p => p.id === task.projectId);
                      const statusLabels = {
                        'beklemede': 'Beklemede',
                        'işlemde': 'İşlemde',
                        'incelemede': 'İncelemede',
                        'tamamlandı': 'Tamamlandı'
                      };
                      const statusColors = {
                        'beklemede': 'bg-gray-100 text-gray-700',
                        'işlemde': 'bg-blue-100 text-blue-700',
                        'incelemede': 'bg-yellow-100 text-yellow-700',
                        'tamamlandı': 'bg-green-100 text-green-700'
                      };
                      
                      // Zorla görev başlığını kısalt
                      const truncateTitle = (title, maxLength = 40) => {
                        if (!title) return '';
                        if (title.length <= maxLength) return title;
                        return title.substring(0, maxLength) + '...';
                      };
                      
                      const displayTitle = truncateTitle(task.title);
                      const displayProjectName = project?.name ? truncateTitle(project.name, 30) : '-';

                      // Kategori filtresi seçiliyse, o kategorideki adımı tamamlayan kullanıcı adı/adları.
                      // Tüm kategoriler seçiliyse farklı adımlarda farklı isimler olacağından gösterilmez.
                      // Renkler, Görevler ekranındaki kullanıcı badge renkleriyle birebir aynı mantıkla atanır.
                      let categoryCompletedUsers = [];
                      if (selectedTag !== 'all') {
                        // Backend ile tutarlı: kullanıcı+kategori başına EN ESKİ tamamlanmış
                        // kaydın tek değeri alınır (revizyon adımları toplanmaz).
                        const byUser = {};
                        (task.assignmentHistory || [])
                          .filter(a => a.completedAt && a.completedTagId === selectedTag)
                          .filter(a => selectedUser === 'all' || a.userId === selectedUser)
                          .forEach(a => {
                            const u = availableUsers.find(x => x.id === a.userId);
                            if (!u) return;
                            const cur = byUser[u.id];
                            if (!cur || a.completedAt < cur.completedAt) {
                              byUser[u.id] = {
                                id: u.id, name: u.name, color: getUserBadgeColor(u.id),
                                completedAt: a.completedAt,
                                qType: a.completedQuantityType,
                                qValue: a.completedQuantityValue,
                              };
                            }
                          });
                        categoryCompletedUsers = Object.values(byUser).map(e => {
                          let qtyLabel = '';
                          if (e.qType === 'süre' && e.qValue) qtyLabel = String(e.qValue);
                          else if (e.qType === 'adet' && e.qValue) qtyLabel = `${e.qValue} adet`;
                          return { ...e, qtyLabel };
                        });
                      }

                      return { task, userCompletedSteps, completedStepsAcrossAll, totalWorkflowSteps, lastActionDate, displayTitle, displayProjectName, statusLabels, statusColors, categoryCompletedUsers };
                    })
                    .filter(Boolean)
                    .sort((a, b) => {
                      if (!a.lastActionDate && !b.lastActionDate) return 0;
                      if (!a.lastActionDate) return 1;
                      if (!b.lastActionDate) return -1;
                      return b.lastActionDate.localeCompare(a.lastActionDate);
                    })
                    .map(({ task, completedStepsAcrossAll, totalWorkflowSteps, lastActionDate, displayTitle, displayProjectName, statusLabels, statusColors, categoryCompletedUsers }) => (
                        <tr 
                          key={task.id} 
                          className="cursor-pointer transition-colors"
                          onClick={() => handleTaskClick(task.id)}
                        >
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div 
                              className="text-sm font-medium text-gray-900 hover:text-cyan-600 mb-1"
                              style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word'
                              }}
                            >
                              {displayTitle}
                            </div>
                            <div className="text-xs text-gray-500">
                              {completedStepsAcrossAll}/{totalWorkflowSteps} adım tamamlandı
                              {categoryCompletedUsers && categoryCompletedUsers.length > 0 && (
                                <span data-testid="report-task-completed-by">
                                  <span className="mx-1 text-gray-400">→</span>
                                  {categoryCompletedUsers.map((u, i) => (
                                    <span key={u.id}>
                                      {i > 0 ? <span className="text-gray-400">, </span> : null}
                                      <span className="font-bold" style={{ color: u.color }}>{u.name}</span>
                                      {u.qtyLabel ? <span className="text-gray-500"> · {u.qtyLabel}</span> : null}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="hidden sm:table-cell px-6 py-4">
                            <div 
                              className="text-sm text-gray-900"
                              style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word'
                              }}
                            >
                              {displayProjectName}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-xs font-medium whitespace-nowrap ${statusColors[task.status] || 'bg-gray-100 text-gray-700'}`}>
                              {statusLabels[task.status] || task.status}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell px-6 py-4 text-sm text-gray-500">
                            {lastActionDate ? new Date(lastActionDate).toLocaleDateString('tr-TR') : '-'}
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : reportData && reportData.tagSummaries && reportData.tagSummaries.length === 0 && (
            <div className="glass rounded-2xl p-12 text-center">
              <Info className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Henüz Tamamlanmış Görev Yok</h3>
              <p className="text-gray-600">Seçtiğiniz filtreye uygun tamamlanmış görev bulunmamaktadır.</p>
            </div>
          )}
        </>
      )}

      {/* Task Detail Drawer */}
      <TaskDrawer
        selectedTaskId={selectedTaskId}
        isOpen={showDrawer}
        drawerRef={drawerRef}
        onClose={handleCloseDrawer}
        onDataChanged={() => {
          // Sessiz güncelleme - loading state tetiklemeden, cache bypass ile
          const params = {
            period, nocache: Date.now(),
            ...(selectedProject !== 'all' && { project_id: selectedProject }),
            ...(selectedTag !== 'all' && { tag_id: selectedTag }),
            ...(selectedUser !== 'all' && { user_id: selectedUser }),
            ...(period === 'custom' && customStartDate && { start_date: customStartDate.toISOString() }),
            ...(period === 'custom' && customEndDate && { end_date: new Date(customEndDate.getTime() + 86400000 - 1).toISOString() })
          };
          axios.get(`${API}/reports/user-summary`, { params }).then(res => {
            setReportData(res.data);
          }).catch(() => {});
        }}
      />
    </div>
  );
}
